// src/pages/DebtStudio/components/DealForm/Refunding.jsx
import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import { DropdownSelect, TextInput, NumberInput } from "components/InputStyled";
import { useIssuesFileManager } from "../fileManager";
import {
  buildRefundedIssueSnapshots,
  getRefundingCandidateMaturities,
  selectedKeysFromRefundedIssues,
} from "pages/DebtStudio/lib/refundingCandidates";
import { matchesIssueIssuer } from "pages/DebtStudio/lib/issuerIdentity";

const MODE_OPTIONS = [
  { value: "manual", label: "Manual Entry" },
  { value: "ds_issue", label: "DebtStudio Issue" },
];

export default function Refunding({
  isReadOnly = false,
  refundingMode = "manual",
  refundingManualDeposits = [],
  currentDealId = null,
  issuerId = null,
  issuerName = "",
  settlementDate = "",
  refundedIssues = [],
  onChangeRefundingMode,
  onChangeRefundingManualDeposits,
  onChangeRefundedIssues,
  manualOnly = false,
}) {
  const rows = useMemo(
    () => refundingManualDeposits || [],
    [refundingManualDeposits]
  );
  const { records, loading, error, refresh } = useIssuesFileManager({ limit: 500 });
  const initialWindow = Number(refundedIssues?.[0]?.eligibility_window_days);
  const [maxDaysBeforeCall, setMaxDaysBeforeCall] = useState(
    Number.isFinite(initialWindow) ? initialWindow : 90
  );
  const [showIneligible, setShowIneligible] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(
    () => new Set(selectedKeysFromRefundedIssues(refundedIssues))
  );

  useEffect(() => {
    setSelectedKeys(new Set(selectedKeysFromRefundedIssues(refundedIssues)));
  }, [refundedIssues]);

  const candidateDeals = useMemo(() => {
    return (records || []).filter((deal) => {
      const financingType = String(deal?.financing_type || "bond").toLowerCase();
      return deal?.id !== currentDealId &&
        matchesIssueIssuer({ deal, issuerId, issuerName }) &&
        financingType === "bond";
    });
  }, [records, issuerId, issuerName, currentDealId]);

  const candidateGroups = useMemo(() => candidateDeals.map((deal) => ({
    deal,
    maturities: getRefundingCandidateMaturities({
      deal,
      settlementDate,
      maxDaysBeforeCall,
    }),
  })).filter((group) => group.maturities.some((m) => m.eligible) || showIneligible), [
    candidateDeals,
    settlementDate,
    maxDaysBeforeCall,
    showIneligible,
  ]);

  const selectedSummary = useMemo(() => {
    const all = candidateGroups.flatMap((group) => group.maturities);
    const selected = all.filter((m) => selectedKeys.has(m.key));
    return {
      count: selected.length,
      principal: selected.reduce((sum, m) => sum + m.principal, 0),
      deposit: selected.reduce((sum, m) => sum + m.depositAmount, 0),
    };
  }, [candidateGroups, selectedKeys]);

  const commitSelection = (nextKeys, windowDays = maxDaysBeforeCall) => {
    setSelectedKeys(nextKeys);
    onChangeRefundedIssues?.(buildRefundedIssueSnapshots({
      deals: candidateDeals,
      selectedKeys: nextKeys,
      settlementDate,
      maxDaysBeforeCall: windowDays,
    }));
  };

  const toggleMaturity = (maturity) => {
    if (isReadOnly || !maturity.eligible) return;
    const next = new Set(selectedKeys);
    if (next.has(maturity.key)) next.delete(maturity.key);
    else next.add(maturity.key);
    commitSelection(next);
  };

  const toggleIssue = (group) => {
    if (isReadOnly) return;
    const eligibleKeys = group.maturities.filter((m) => m.eligible).map((m) => m.key);
    const allSelected = eligibleKeys.length > 0 && eligibleKeys.every((key) => selectedKeys.has(key));
    const next = new Set(selectedKeys);
    eligibleKeys.forEach((key) => allSelected ? next.delete(key) : next.add(key));
    commitSelection(next);
  };

  const money = (value) => Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  const updateRow = (idx, key, val) => {
    if (isReadOnly) return;
    if (!onChangeRefundingManualDeposits) return;
    onChangeRefundingManualDeposits(
      rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r))
    );
  };

  const addRow = () => {
    if (isReadOnly) return;
    if (!onChangeRefundingManualDeposits) return;
    onChangeRefundingManualDeposits([...rows, { issueName: "", amount: "" }]);
  };

  const removeRow = (idx) => {
    if (isReadOnly) return;
    if (!onChangeRefundingManualDeposits) return;
    onChangeRefundingManualDeposits(rows.filter((_, i) => i !== idx));
  };

  return (
    <>
      <SectionCard title="Refunded Issues">
        <div className="max-w-[240px]">
          <div className="text-xs text-muted mb-1">Issue Selection Mode</div>

          <DropdownSelect
            value={refundingMode}
            onChange={(v) => {
              if (isReadOnly) return;
              onChangeRefundingMode && onChangeRefundingMode(v);
            }}
            options={manualOnly ? MODE_OPTIONS.slice(0, 1) : MODE_OPTIONS}
            className="w-full"
            disabled={isReadOnly || manualOnly}
          />
        </div>

        {!manualOnly && refundingMode === "ds_issue" && (
          <div className="mt-5 space-y-5">
            <div className="flex flex-wrap items-end gap-4">
              <div className="w-52">
                <div className="text-xs text-muted mb-1">Maximum Days Before Call</div>
                <NumberInput
                  value={maxDaysBeforeCall}
                  onChange={(value) => {
                    const nextDays = Math.max(0, Number(value) || 0);
                    setMaxDaysBeforeCall(nextDays);
                    if (selectedKeys.size) commitSelection(selectedKeys, nextDays);
                  }}
                  type="integer"
                  disabled={isReadOnly}
                  className="w-full text-right"
                />
              </div>
              <label className="flex items-center gap-2 h-8 text-sm">
                <input
                  type="checkbox"
                  checked={showIneligible}
                  onChange={(event) => setShowIneligible(event.target.checked)}
                />
                Show ineligible maturities
              </label>
              <button type="button" className="btn btn-ghost" onClick={refresh} disabled={loading}>
                Refresh Candidates
              </button>
            </div>

            {!settlementDate && (
              <div className="text-sm text-warning">Enter a Delivery Date under Dates &amp; Terms to determine eligibility.</div>
            )}
            {error && <div className="text-sm text-danger">Unable to load candidate issues: {error.message}</div>}
            {loading && <div className="text-sm text-muted">Loading candidate issues…</div>}

            {!loading && settlementDate && (issuerId || issuerName) && candidateGroups.length === 0 && (
              <div className="text-sm text-muted">No callable maturities meet the current settlement-date window.</div>
            )}

            <div className="space-y-3">
              {candidateGroups.map((group) => {
                const eligible = group.maturities.filter((m) => m.eligible);
                const allSelected = eligible.length > 0 && eligible.every((m) => selectedKeys.has(m.key));
                return (
                  <div key={group.deal.id} className="rounded-md border border-input overflow-hidden">
                    <label className="flex items-center gap-3 px-3 py-2 bg-surface-2 font-medium text-sm">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => toggleIssue(group)}
                        disabled={isReadOnly || eligible.length === 0}
                      />
                      <span>{group.deal.name || "(Untitled Issue)"}</span>
                      <span className="ml-auto text-xs text-muted">{eligible.length} eligible</span>
                    </label>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs text-muted border-t border-b border-input">
                          <tr>
                            <th className="p-2 text-left">Include</th>
                            <th className="p-2 text-left">Maturity</th>
                            <th className="p-2 text-right">Principal</th>
                            <th className="p-2 text-right">Coupon</th>
                            <th className="p-2 text-left">Call Date</th>
                            <th className="p-2 text-right">Call Price</th>
                            <th className="p-2 text-right">Est. Deposit</th>
                            {showIneligible && <th className="p-2 text-left">Status</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {group.maturities.filter((m) => showIneligible || m.eligible).map((m) => (
                            <tr key={m.key} className="border-b border-input last:border-b-0">
                              <td className="p-2"><input type="checkbox" checked={selectedKeys.has(m.key)} onChange={() => toggleMaturity(m)} disabled={isReadOnly || !m.eligible} /></td>
                              <td className="p-2">{m.maturityDate || "—"}</td>
                              <td className="p-2 text-right tabular-nums">{money(m.principal)}</td>
                              <td className="p-2 text-right tabular-nums">{m.coupon.toFixed(3)}%</td>
                              <td className="p-2">{m.callDate || "—"}</td>
                              <td className="p-2 text-right tabular-nums">{m.callPrice.toFixed(3)}</td>
                              <td className="p-2 text-right tabular-nums">{money(m.depositAmount)}</td>
                              {showIneligible && <td className="p-2 text-xs text-muted">{m.eligible ? "Eligible" : m.ineligibleReason}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedSummary.count > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-md border border-input p-3 text-sm">
                <div><div className="text-xs text-muted">Selected Maturities</div><div className="font-medium">{selectedSummary.count}</div></div>
                <div><div className="text-xs text-muted">Refunded Principal</div><div className="font-medium tabular-nums">{money(selectedSummary.principal)}</div></div>
                <div><div className="text-xs text-muted">Estimated Deposit</div><div className="font-medium tabular-nums">{money(selectedSummary.deposit)}</div></div>
              </div>
            )}
            <div className="text-xs text-muted">
              Estimated deposit includes principal and call premium. Interest through redemption and escrow investment earnings will be added with the escrow calculation phase.
            </div>
          </div>
        )}

        {refundingMode === "manual" && (
          <div className="mt-6">
            {rows.length > 0 && (
              <div className="hidden md:grid grid-cols-[2fr_1fr_0.4fr] gap-3 mb-2 text-xs text-muted">
                <div>Issue Name</div>
                <div>Refunding Deposit Amount ($)</div>
                <div />
              </div>
            )}

            <div className="space-y-2">
              {rows.map((r, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 md:grid-cols-[2fr_1fr_0.4fr] gap-3 items-center"
                >
                  <TextInput
                    value={r.issueName || ""}
                    onChange={(v) => updateRow(idx, "issueName", v)}
                    placeholder="Issue Name"
                    className="w-full"
                    disabled={isReadOnly}
                  />

                  <NumberInput
                    value={r.amount ?? ""}
                    onChange={(n) => updateRow(idx, "amount", n)}
                    type="decimal"
                    className="w-full text-right"
                    disabled={isReadOnly}
                  />

                  <div className="flex justify-end">
                    {isReadOnly ? null : (
                      <button
                        type="button"
                        className="btn btn-ghost text-xs"
                        onClick={() => removeRow(idx)}
                        tabIndex={-1}
                        style={{ padding: "0 6px" }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <button
                type="button"
                className="btn btn-primary"
                onClick={addRow}
                disabled={isReadOnly}
                title={isReadOnly ? "Read-only" : undefined}
              >
                + Add Issue
              </button>
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
}
